/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import util from "../../util/util.js";

/*
isComponent parameter indicates that this model is being used as a normal KnockoutJS component.

Previously this was more of a 'helper' model used in various different ways and you had to provide your own template.
*/
var SortableTableViewModel = function(columnParams, params, isComponent) {
	var self = this;

	params = params || {};

	self.emptyMessage = params.emptyMessage || "No items to show";

	self.sortDescriptors = ko.observable(params.sortDescriptors || []);

	self.columns = columnParams.map(function(params) {
		return new ColumnViewModel(params, self);
	});

	self.tableClass = ko.observable(params.tableClass || "");

	self.rowClick = params.rowClick || function() {};

	self.pageIndex = ko.observable(0);

	// column parameter must be an column id (string)
	self.toggleSort = function(column) {
		var descriptors = self.sortDescriptors();
		var descriptor = descriptors.find(function(descriptor) {
			return descriptor.column == column;
		});

		descriptors = descriptors.filter(function(descriptor) {
			return descriptor.column != column;
		});

		if(descriptor) {
			descriptor.ascending = !descriptor.ascending;
		} else {
			descriptor = {
				column: column,
				ascending: true
			};
		}
		descriptors.push(descriptor);

		self.sortDescriptors(descriptors);

		self.pageIndex(0);
	};

	self.column = function(id) {
		return self.columns.find(function(column) {
			return column.params.id == id;
		});
	};

	// Helpers to sort items
	self.sortItems = function(items) {
		items = [ ...items ]

		// Sort surveys according to table sort criteria
		self.sortDescriptors().forEach(function(descriptor) {
			if(!descriptor.ascending) items.reverse();
			var column = self.column(descriptor.column);
			items.sort(function(a, b) {
				let aa = column.params.value(a);
				let bb = column.params.value(b);
				return (aa > bb) ? 1 : (aa < bb) ? -1 : 0;
			});
			if(!descriptor.ascending) items.reverse();
		});

		return items;
	};

	self.sortedItems = function(items) {
		return ko.pureComputed(function() {
			return self.sortItems(items());
		});
	};

	self.rowsHTML = function(sortedItems) {
		var html = "";

		ko.unwrap(sortedItems()).forEach(function(row, i) {
			// Add class per row if 'classesForRow' parameter supplied
			var classes = params.classesForRow && params.classesForRow(row);
			var classAttr = classes? `class='${classes}'` : '';

			html += `<tr data-i='${i}' ${classAttr}>`;
			self.columns.forEach(function(col) {
				html += `<td class='col-${col.id}'>${col.html(row)}</td>`;
			});
			html += "</tr>";
		});
		return html;
	}

	self.eventRowIndex = function(evt) {
		var el = evt.target;
		while(el && el.tagName != 'TR') el = el.parentElement;
		return el && el.getAttribute('data-i');
	}

	self.eventColumnID = function(evt) {
		var el = evt.target, i = -1;
		while(el && el.tagName != 'TD') el = el.parentElement;
		while(el) {
			el = el.previousElementSibling;
			i++;
		}
		return self.columns[i] && self.columns[i].id;
	}

	if(isComponent) {
		self.items = params.items;

		if(params.pageSize) {
			function changePage(delta, checkOnly) {
				var pageCount = self.items().length / params.pageSize;
				var newPage = self.pageIndex() + delta;
				var ok = newPage >= 0 && newPage < pageCount;
				if(checkOnly) {
					return ok;
				} else if(ok) {
					self.pageIndex(newPage);
				}
			}

			self.paginated = true;
			self.pageLabel = ko.pureComputed(function() {
				let itemCount = self.items().length;
				let page = self.pageIndex();
				let min = page * params.pageSize + 1;
				let max = Math.min((page + 1) * params.pageSize, itemCount);
				return `${min}-${max} of ${itemCount}`;
			});
			self.prevPage = () => changePage(-1);
			self.enablePrevPage = () => changePage(-1, true);
			self.nextPage = () => changePage(1);
			self.enableNextPage = () => changePage(1, true);

			self.displayedItems = ko.pureComputed(() => {
				var items = ko.unwrap(self.sortedItems(params.items));
				return items.slice(self.pageIndex() * params.pageSize, (self.pageIndex() + 1) * params.pageSize);
			});
		} else {
			self.paginated = false;
			self.displayedItems = self.sortedItems(self.items);
		}
	}
};

var ColumnViewModel = function(params, parent) {
	var self = this;
	self.params = params;

	self.toggleSort = function() {
		if(self.params.sortable === false) return;

		parent.toggleSort(self.params.id);
	};

	self.sortClass = ko.pureComputed(function() {
		if(self.params.sortable === false) return '';

		var descriptors = parent.sortDescriptors();
		var descriptor = descriptors[descriptors.length - 1];

		if(descriptor && descriptor.column == params.id) {
			return descriptor.ascending ? 'sort-down' : 'sort-up';
		} else {
			return '';
		}
	});

	self.headerClass = ko.pureComputed(function() {
		return self.columnClass + " " + self.sortClass();
	});

	self.columnClass = 'col-' + params.id;
	self.columnStyle = params.style;

	self.formatted = function(x) {
		if(params.formatted) {
			return params.formatted(x);
		} else {
			return params.value(x);
		}
	}

	self.html = function(x) {
		if(params.html) {
			return params.html(x);
		} else {
			return util.escapeHtml(self.formatted(x));
		}
	};

	self.name = params.name;

	self.click = params.click || function() {};

	self.id = params.id;
	self.custom = params.custom; // Column that will be custom rendered inside the template
};

ko.components.register('sortable-table', {
	viewModel: function(params) {
		return new SortableTableViewModel(params.columns, params, true);
	},
	template: require('../../../templates/shared/sortable-table.html')
});

export default SortableTableViewModel;
